import { useState, useEffect, useCallback, useMemo } from "react";
import { Sparkles, Leaf, Check, Loader2, Clock, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { TrialCalendarOptions } from "../components/TrialCalendarOptions";
import { useSiteTexts } from "../lib/useSiteTexts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Trial Booking — Patch 2 + 2.1
// When admin has switched trial booking mode to "direct" AND `trial_fee_enabled`
// is false, the form swaps from the enquiry flow to a slot-picker that books
// the appointment atomically via `POST /api/booking/trial-direct`.
// Patch 2.1: the slot picker is now a responsive month-grid calendar. Dates
// without any genuinely available slot (from `/api/booking/trial-slots/available`,
// which already filters blocked/booked/conflicting slots server-side) are
// disabled. Selecting an available date reveals the time pills below.
// The enquiry flow is retained unchanged for mode="enquiry" (or when a paid
// trial fee is required — Patch 3 will handle that).

// ── Calendar helpers ─────────────────────────────────────────────────────
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_LABELS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function ymd(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

// Trial Portal Patch 1.3 — Calendar bug fix. All customer-facing calendar
// logic MUST anchor on the Melbourne calendar date, not the browser local
// date. Previously `today = new Date()` + `.toISOString().slice(0,10)`
// silently produced UTC-shifted dates, causing valid days (e.g. 31 Jul
// when the user booked on 30 Jul) to be marked as past on the frontend
// even though the backend returned bookable slots for them.
function melbourneToday() {
  // Intl "en-CA" reliably returns YYYY-MM-DD for the given TZ.
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Australia/Melbourne",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date()); // "2026-07-31" style
}

/** Build a 6-row × 7-col grid of Date objects for the visible month.
 *  Weeks start on Monday to match Australian convention. */
function buildMonthGrid(year, month) {
  const first = new Date(year, month, 1);
  // JS getDay(): 0=Sun … 6=Sat. Convert to 0=Mon … 6=Sun.
  const startOffset = (first.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }
  return cells;
}

export default function TrialBooking() {
  const t = useSiteTexts();
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [responseHours, setResponseHours] = useState(24);
  const [trialEnabled, setTrialEnabled] = useState(true);
  const [dateError, setDateError] = useState("");

  // Patch 2.3 (Issue 2 fix) — booking mode is UNRESOLVED until /booking/types
  // returns. Rendering is gated on this so the enquiry form can never flash
  // before the true mode is known. `null` = still loading, "enquiry" | "direct"
  // = resolved. `trialFeeEnabled` is only meaningful once mode is resolved.
  const [modeResolved, setModeResolved] = useState(false);
  const [bookingMode, setBookingMode] = useState(null);
  const [trialFeeEnabled, setTrialFeeEnabled] = useState(false);
  const [trialFeeAmount, setTrialFeeAmount] = useState(0);

  // Direct-mode slot cache is keyed by "YYYY-MM" so month navigation can fetch
  // on demand without refetching months already loaded.
  const [slotsByMonth, setSlotsByMonth] = useState({}); // { "2026-12": [slot, …] }
  const [monthsLoading, setMonthsLoading] = useState({}); // { "2026-12": true }
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [directBooking, setDirectBooking] = useState(null); // success payload

  // Patch 2.4 → 3.1 — hint for large availability gaps. Threshold lowered from
  // 21 days (Patch 2.4) to 14 days (Patch 3.1) per admin feedback: 3 weeks was
  // too conservative and let 2-week gaps pass unannounced.
  const GAP_THRESHOLD_DAYS = 14;
  const [nextAvailable, setNextAvailable] = useState(null); // { date, start_time } | null

  // Patch 3.1 — admin-configurable display label for calendar dates that have
  // no bookable slots. Sourced from /booking/types → trial_unavailable_date_label.
  const [unavailableLabel, setUnavailableLabel] = useState("Unavailable");

  // Patch 2.1: month calendar navigation + selected date
  const todayStr = melbourneToday();
  const today = useMemo(() => {
    const [y, m, d] = todayStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  }, [todayStr]); // Melbourne-anchored; stable across browser TZ / DST.
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth()); // 0-indexed
  const [selectedDate, setSelectedDate] = useState(""); // "YYYY-MM-DD"

  const PAST_DATE_MSG = "Trial appointments can only be requested for today or a future date.";

  const handleDateInvalid = (e) => { e.preventDefault(); e.target.setCustomValidity(PAST_DATE_MSG); setDateError(PAST_DATE_MSG); };
  const handleDateInput = (e) => { e.target.setCustomValidity(""); setDateError(""); };

  const monthKey = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;

  // Patch 2.3 (Issue 1 fix) — fetch availability scoped to the given month.
  // Uses explicit from_date/to_date so December 2026 (and any month, no matter
  // how far ahead) actually loads. Caches per-month so nav ping-pong is cheap.
  const loadMonth = useCallback(async (y, m) => {
    const key = monthKey(y, m);
    // Skip if we already have it or a fetch is already in flight.
    if (slotsByMonth[key] || monthsLoading[key]) return;
    setMonthsLoading(prev => ({ ...prev, [key]: true }));
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    // Never ask the API for past dates — the server floors these anyway.
    const t = new Date(); t.setHours(0, 0, 0, 0);
    // Patch 1.3 — use ymd() on local components; toISOString() would shift TZ.
    const fromDate = ymd((first < t ? t : first).getFullYear(), (first < t ? t : first).getMonth(), (first < t ? t : first).getDate());
    const toDate = ymd(last.getFullYear(), last.getMonth(), last.getDate());
    try {
      const r = await fetch(`${API}/booking/trial-slots/available?from_date=${fromDate}&to_date=${toDate}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSlotsByMonth(prev => ({ ...prev, [key]: d.slots || [] }));
    } catch (err) {
      console.warn(`[TrialBooking] month ${key} fetch failed:`, err);
      // Cache an empty list so we don't refetch on every navigation click; the
      // user can still hit Refresh via re-navigation.
      setSlotsByMonth(prev => ({ ...prev, [key]: [] }));
    } finally {
      setMonthsLoading(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  }, [slotsByMonth, monthsLoading]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Patch 3 — release the trial hold if the customer bailed out of Stripe.
      const params = new URLSearchParams(window.location.search);
      if (params.get("cancelled") === "1") {
        const holdId = params.get("hold_id") || sessionStorage.getItem("mahra_trial_hold_id") || "";
        if (holdId) {
          try {
            await fetch(`${API}/booking/trial-direct/cancel`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ hold_id: holdId }),
            });
          } catch (err) {
            console.warn("[TrialBooking] hold release failed:", err);
          }
        }
        sessionStorage.removeItem("mahra_trial_hold_id");
        toast.info("Your held slot has been released. Feel free to pick another time.");
        // Strip the cancelled params from the URL so a refresh doesn't re-fire.
        window.history.replaceState({}, "", window.location.pathname);
      }
      // Patch 2.2 (Issue 3 fix) — decide booking mode from the FAST /booking/types
      // endpoint (settings-only). Rendering is gated on `modeResolved` (Patch 2.3
      // Issue 2 fix) so the enquiry form never flashes before mode is known.
      try {
        // Patch 2 — reuses shared settings cache to avoid a duplicate
        // /booking/types call when navigating between pages that also fetch it.
        const { fetchBookingSettings } = await import("../lib/settingsCache");
        const d = await fetchBookingSettings(API);
        if (cancelled) return;
        const trial = (d.types || []).find(t => t.key === "trial");
        if (!trial) setTrialEnabled(false);
        if (d.trial_response_hours) setResponseHours(d.trial_response_hours);
        setBookingMode(d.trial_booking_mode || "enquiry");
        setTrialFeeEnabled(!!d.trial_fee_enabled);
        setTrialFeeAmount(Number(d.trial_fee_amount || 0));
        if (d.trial_unavailable_date_label && String(d.trial_unavailable_date_label).trim()) {
          setUnavailableLabel(String(d.trial_unavailable_date_label).trim());
        }
      } catch (err) {
        console.warn("[TrialBooking] booking-types fetch failed:", err);
        // On failure default to enquiry so the customer still has a working path.
        setBookingMode("enquiry");
      } finally {
        if (!cancelled) setModeResolved(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Patch 3 — direct flow is used for both free AND paid direct bookings.
  // The payment hop happens inside handleSubmit when trialFeeEnabled=true.
  const useDirectFlow = bookingMode === "direct";

  // Patch 2.3 — trigger the initial month fetch once mode is confirmed direct.
  useEffect(() => {
    if (!modeResolved || !useDirectFlow) return;
    loadMonth(viewYear, viewMonth);
  }, [modeResolved, useDirectFlow, loadMonth, viewYear, viewMonth]);

  // Fetch whenever the visible month changes.
  useEffect(() => {
    if (!modeResolved || !useDirectFlow) return;
    loadMonth(viewYear, viewMonth);
  }, [viewYear, viewMonth, modeResolved, useDirectFlow, loadMonth]);

  // Flatten cached months into a single list for downstream helpers.
  const availableSlots = useMemo(
    () => Object.values(slotsByMonth).flat(),
    [slotsByMonth],
  );

  // Patch 2.1 — first-load auto-jump: if the visible month has no availability
  // but a later month does (already cached), jump to the first month that has
  // any. Only jumps forward. Deliberately does NOT re-run on every fetch — it
  // fires once per unique set of month keys so admin-far-ahead slots surface
  // without stealing the user's navigation once they've started clicking.
  const cachedMonthKeys = useMemo(() => Object.keys(slotsByMonth).sort(), [slotsByMonth]);
  useEffect(() => {
    if (!useDirectFlow || availableSlots.length === 0) return;
    // Don't auto-jump once the user has clicked Next / Prev (viewMonth diverges
    // from today's month). This preserves user navigation intent.
    if (viewYear !== today.getFullYear() || viewMonth !== today.getMonth()) return;
    const currentKey = monthKey(viewYear, viewMonth);
    const currentMonthHasSlots = (slotsByMonth[currentKey] || []).length > 0;
    if (currentMonthHasSlots) return;
    const firstDate = [...availableSlots].sort((a, b) => a.date.localeCompare(b.date))[0].date;
    const [yy, mm] = firstDate.split("-").map(Number);
    setViewYear(yy); setViewMonth(mm - 1);
  }, [cachedMonthKeys, availableSlots, useDirectFlow, viewYear, viewMonth, slotsByMonth, today]);

  // Clear selectedDate if it disappears from the current cache (409 refresh
  // etc.) — but ONLY once the month containing selectedDate has actually been
  // fetched. Otherwise the banner-jump path would wipe a fresh pre-selection
  // before Nov's fetch settles.
  useEffect(() => {
    if (!selectedDate) return;
    const selKey = selectedDate.slice(0, 7);
    const cached = slotsByMonth[selKey];
    if (!cached) return; // target month not loaded yet — don't wipe.
    if (!cached.some(s => s.date === selectedDate)) {
      setSelectedDate("");
    }
  }, [slotsByMonth, selectedDate]);

  // Patch 2.4 — pull the earliest open date globally so we can surface a hint
  // when the current viewed month has a long gap to the next real availability.
  // Refetches when the currently-viewed month or its last-available date
  // changes so the hint always references "the next open date AFTER what the
  // customer can already see".
  useEffect(() => {
    if (!modeResolved || !useDirectFlow) return;
    // Wait until the current month has actually resolved so we know the
    // reference date; otherwise we may briefly ask for the wrong window.
    const currentKey = monthKey(viewYear, viewMonth);
    if (monthsLoading[currentKey]) return;
    const currentSlots = slotsByMonth[currentKey] || [];
    let after;
    if (currentSlots.length > 0) {
      // Ask for the next open date AFTER the last date currently visible.
      const lastDate = [...new Set(currentSlots.map(s => s.date))].sort().slice(-1)[0];
      const d = new Date(lastDate + "T00:00:00");
      d.setDate(d.getDate() + 1);
      after = ymd(d.getFullYear(), d.getMonth(), d.getDate());
    } else {
      // No availability in this month — look for the next open date from the
      // later of (Melbourne today, first-of-viewed-month).
      const monthStart = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-01`;
      after = monthStart > todayStr ? monthStart : todayStr;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/booking/trial-slots/next-available?after=${after}`);
        const d = await r.json();
        if (cancelled) return;
        setNextAvailable(d.date ? d : null);
      } catch (err) {
        console.warn("[TrialBooking] next-available fetch failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [modeResolved, useDirectFlow, viewYear, viewMonth, slotsByMonth, monthsLoading, today, directBooking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number");
      return;
    }

    if (useDirectFlow) {
      if (!selectedSlotId) {
        toast.error("Please choose an available time slot.");
        return;
      }
      setSubmitting(true);
      try {
        // Patch 3 — when a trial fee is required, hop through Stripe Checkout.
        // Free direct flow keeps calling /trial-direct as before.
        const endpoint = trialFeeEnabled ? `${API}/booking/trial-direct/checkout` : `${API}/booking/trial-direct`;
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            slot_id: selectedSlotId,
            name: form.name, email: form.email, phone: form.phone,
            notes: form.notes,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          const detail = data.detail;
          const msg = typeof detail === "string" ? detail : (detail?.message || "Failed to book slot");
          if (res.status === 409) {
            toast.error(msg);
            const key = monthKey(viewYear, viewMonth);
            setSlotsByMonth(prev => { const n = { ...prev }; delete n[key]; return n; });
            setSelectedSlotId("");
            await loadMonth(viewYear, viewMonth);
          } else {
            throw new Error(msg);
          }
          return;
        }
        if (trialFeeEnabled) {
          // Persist the hold id so the cancel-URL handler can release the slot
          // if the customer bails out of Stripe.
          if (data.hold_id) sessionStorage.setItem("mahra_trial_hold_id", data.hold_id);
          window.location.href = data.checkout_url;
          return;
        }
        setDirectBooking(data);
        setSubmitted(true);
      } catch (err) {
        toast.error(err.message || "Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Enquiry flow (unchanged from Patch 1)
    if (!form.preferred_date) {
      setDateError("Please choose your preferred trial date.");
      toast.error("Please choose your preferred trial date.");
      return;
    }
    if (!form.preferred_time) {
      toast.error("Please choose your preferred trial time.");
      return;
    }
    {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const picked = new Date(form.preferred_date + "T00:00:00");
      if (picked < today) {
        setDateError(PAST_DATE_MSG);
        toast.error(PAST_DATE_MSG);
        return;
      }
    }
    setDateError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/booking/trial-enquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to submit");
      setResponseHours(data.response_hours || 24);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  // Patch 2.3 — derived loading/empty flags scoped to the currently viewed month.
  const currentMonthKey = monthKey(viewYear, viewMonth);
  const currentMonthLoading = !!monthsLoading[currentMonthKey];
  const currentMonthSlots = slotsByMonth[currentMonthKey] || [];
  const currentMonthHasAnySlots = currentMonthSlots.length > 0;

  // Patch 2.4 — gap-hint decision. Show the "More trial appointments available
  // from …" banner ONLY when:
  //  * the viewed month has already been fetched (no flash while loading),
  //  * we know a real next-available date exists globally,
  //  * that date is more than GAP_THRESHOLD_DAYS after either:
  //      (a) the last available date in the current viewed month, or
  //      (b) if the current viewed month has no availability at all, the
  //          reference is `max(today, first-of-viewed-month)`.
  //  * the next-available date is not already in the current month.
  const gapHintTarget = useMemo(() => {
    if (!useDirectFlow || currentMonthLoading || !nextAvailable?.date) return null;
    const [ny, nm] = nextAvailable.date.split("-").map(Number);
    if (ny === viewYear && (nm - 1) === viewMonth) return null;
    if ((ny * 12 + (nm - 1)) < (viewYear * 12 + viewMonth)) return null;

    const referenceStr = (() => {
      if (currentMonthHasAnySlots) {
        return [...new Set(currentMonthSlots.map(s => s.date))].sort().slice(-1)[0];
      }
      const monthStart = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-01`;
      return monthStart > todayStr ? monthStart : todayStr;
    })();

    const refD = new Date(referenceStr + "T00:00:00");
    const nextD = new Date(nextAvailable.date + "T00:00:00");
    const gapDays = Math.round((nextD - refD) / (1000 * 60 * 60 * 24));
    if (gapDays <= GAP_THRESHOLD_DAYS) return null;
    return { date: nextAvailable.date, gapDays };
  }, [useDirectFlow, currentMonthLoading, currentMonthHasAnySlots, currentMonthSlots, nextAvailable, viewYear, viewMonth, today]);

  const jumpToNextAvailable = () => {
    if (!gapHintTarget) return;
    const [yy, mm] = gapHintTarget.date.split("-").map(Number);
    setViewYear(yy); setViewMonth(mm - 1);
    setSelectedDate(gapHintTarget.date);
  };

  if (!trialEnabled) {
    return (
      <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <Sparkles className="w-8 h-8 text-[#C9A0A0]/40 mx-auto mb-4" strokeWidth={1} />
          <p className="text-[14px] text-[#676664]/60">Trial appointments are not available at the moment. Please check back later.</p>
          <Link to="/" className="inline-block mt-6 text-[11px] text-[#9A7B4F] tracking-wider uppercase hover:text-[#8B6B3F]">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  // Patch 2.3 (Issue 2 fix) — Mahra-styled skeleton while trial booking mode
  // is still being resolved from /booking/types. Renders neither the enquiry
  // form nor the direct-booking calendar until the true mode is known, so a
  // customer on a slow connection never sees the wrong UI flash before the
  // correct one appears.
  if (!modeResolved) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]" data-testid="trial-mode-loading">
        <div className="pt-12 pb-8 sm:pt-16 sm:pb-10 text-center px-4" style={{ backgroundColor: "#C9A0A020" }}>
          <Leaf className="w-5 h-5 text-[#C5D5CC] mx-auto mb-3 animate-pulse" strokeWidth={1.5} fill="#C5D5CC" />
          <h1 className="text-3xl sm:text-4xl tracking-[0.08em] text-[#282828] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {t("trial_booking_heading", "Book a Trial")}
          </h1>
          <p className="text-[13px] text-[#676664]/60 max-w-lg mx-auto leading-relaxed">Preparing your booking experience…</p>
        </div>
        <div className="max-w-lg mx-auto px-3 sm:px-4 py-10">
          <div className="bg-white border border-[#282828]/8 p-8 space-y-4">
            <div className="h-3 w-24 bg-[#282828]/8" />
            <div className="h-10 bg-[#282828]/6" />
            <div className="h-3 w-16 bg-[#282828]/8" />
            <div className="h-10 bg-[#282828]/6" />
            <div className="h-3 w-20 bg-[#282828]/8" />
            <div className="h-10 bg-[#282828]/6" />
            <div className="flex items-center justify-center pt-4">
              <Loader2 className="w-5 h-5 animate-spin text-[#9A7B4F]" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center px-4">
        <div className="text-center max-w-md" data-testid="trial-success">
          <div className="w-16 h-16 mx-auto mb-6 bg-[#B8C9BD]/20 flex items-center justify-center">
            <Check className="w-7 h-7 text-[#6B8F71]" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl tracking-[0.08em] text-[#282828] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
            {directBooking ? "Trial Booked" : "Thank You"}
          </h1>
          {directBooking ? (
            <>
              <p className="text-[14px] text-[#676664]/70 mb-2 leading-relaxed">Your trial is confirmed.</p>
              <p className="text-[15px] text-[#9A7B4F] font-medium mb-8" data-testid="direct-booking-msg">
                {new Date(directBooking.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}<br />
                {directBooking.start_time} – {directBooking.end_time} (AEST)
              </p>
              {/* Patch A.1 — Multi-option Add to Calendar for free direct booking */}
              <TrialCalendarOptions
                details={{
                  date: directBooking.date,
                  startTime: directBooking.start_time,
                  durationMinutes: directBooking.duration_minutes,
                  customerName: form.name,
                  customerEmail: form.email,
                }}
              />
            </>
          ) : (
            <>
              <p className="text-[14px] text-[#676664]/70 mb-2 leading-relaxed">Your trial enquiry has been received.</p>
              <p className="text-[15px] text-[#9A7B4F] font-medium mb-8" data-testid="response-time-msg">
                We&apos;ll contact you within {responseHours} {responseHours === 1 ? "hour" : "hours"} to arrange your trial.
              </p>
            </>
          )}
          <Link
            to="/"
            className="inline-block text-[11px] tracking-[0.14em] uppercase text-[#282828]/60 border-b border-[#282828]/20 pb-1 hover:text-[#9A7B4F] hover:border-[#9A7B4F]"
          >
            Continue Browsing
          </Link>
        </div>
      </div>
    );
  }

  // Group direct-mode slots by date for the picker
  const slotsByDate = availableSlots.reduce((acc, s) => {
    (acc[s.date] = acc[s.date] || []).push(s);
    return acc;
  }, {});
  const slotDates = Object.keys(slotsByDate).sort();

  // Patch 2.3 — derived loading/empty flags computed above (moved before early
  // returns to satisfy React hooks rule ordering).

  // Patch 2.1 — month grid cells for the currently viewed month
  const monthGrid = buildMonthGrid(viewYear, viewMonth);
  // Patch 1.3 — todayStr comes from Melbourne (see top of component).
  // Duplicate declaration removed; the Melbourne-anchored value is used below.
  const canGoPrev = (() => {
    // Allow prev only if the previous month contains today or a future date.
    const prev = new Date(viewYear, viewMonth - 1, 1);
    const prevEnd = new Date(viewYear, viewMonth, 0);
    return prevEnd >= today;
  })();
  const gotoPrev = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
  };
  const gotoNext = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear()); setViewMonth(d.getMonth());
  };

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      {/* Hero */}
      <div className="pt-12 pb-8 sm:pt-16 sm:pb-10 text-center px-4" style={{ backgroundColor: "#C9A0A020" }}>
        <Leaf className="w-5 h-5 text-[#C5D5CC] mx-auto mb-3" strokeWidth={1.5} fill="#C5D5CC" />
        <h1 className="text-3xl sm:text-4xl tracking-[0.08em] text-[#282828] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
          {t("trial_booking_heading", "Book a Trial")}
        </h1>
        <p className="text-[13px] text-[#676664]/60 max-w-lg mx-auto leading-relaxed">
          {useDirectFlow
            ? t("trial_booking_intro", "Choose a date with available slots, pick a time, and your trial will be booked instantly.")
            : t("trial_booking_intro", "Trial appointments are arranged personally to ensure the best experience. Submit your request, and we'll coordinate a suitable time with you.")}
        </p>
      </div>

      <div className={`${useDirectFlow ? "max-w-xl" : "max-w-lg"} mx-auto px-3 sm:px-4 py-10`}>
        <form onSubmit={handleSubmit} className="bg-white border border-[#282828]/8 p-4 sm:p-8 space-y-5 overflow-hidden w-full max-w-full box-border" data-testid={useDirectFlow ? "trial-direct-form" : "trial-enquiry-form"}>
          <div className="w-full">
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Full Name *</label>
            <input type="text" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full max-w-full box-border border border-[#282828]/12 px-3 py-3 text-[13px] text-[#282828] focus:border-[#9A7B4F] focus:outline-none bg-transparent"
              placeholder="Your name" data-testid="trial-name" />
          </div>

          <div className="w-full">
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Email *</label>
            <input type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full max-w-full box-border border border-[#282828]/12 px-3 py-3 text-[13px] text-[#282828] focus:border-[#9A7B4F] focus:outline-none bg-transparent"
              placeholder="your@email.com" data-testid="trial-email" />
          </div>

          <div className="w-full">
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Phone *</label>
            <input type="tel" required value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
              className="w-full max-w-full box-border border border-[#282828]/12 px-3 py-3 text-[13px] text-[#282828] focus:border-[#9A7B4F] focus:outline-none bg-transparent"
              placeholder="04XX XXX XXX" data-testid="trial-phone" />
          </div>

          {useDirectFlow ? (
            /* Patch 2.1 — Month calendar + time slot picker for direct mode.
               Patch 2.3 — always render the calendar shell so the user never
               sees an empty-state flash while a month is loading. Loading is
               indicated by a subtle spinner in the month header and by dates
               remaining muted until the fetch settles. */
            <div className="w-full" data-testid="direct-slot-picker">
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-2">
                Choose a date &amp; time <span className="normal-case text-[#B85C5C]">*</span>
              </label>

              <>
                <div className="border border-[#282828]/10 bg-white" data-testid="trial-calendar">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-[#282828]/8">
                    <button
                      type="button"
                      onClick={gotoPrev}
                      disabled={!canGoPrev}
                      className="p-1.5 text-[#282828] hover:bg-[#FAF8F0] disabled:opacity-25 disabled:cursor-not-allowed"
                      aria-label="Previous month"
                      data-testid="calendar-prev-month"
                    >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                    <div className="text-[13px] tracking-[0.08em] text-[#282828] flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }} data-testid="calendar-month-label">
                      {MONTH_LABELS[viewMonth]} {viewYear}
                      {currentMonthLoading && (
                        <Loader2 className="w-3 h-3 animate-spin text-[#9A7B4F]" data-testid="calendar-month-loading" />
                      )}
                    </div>
                      <button
                        type="button"
                        onClick={gotoNext}
                        className="p-1.5 text-[#282828] hover:bg-[#FAF8F0]"
                        aria-label="Next month"
                        data-testid="calendar-next-month"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 border-b border-[#282828]/8 bg-[#FAF8F0]">
                      {WEEKDAY_LABELS.map(w => (
                        <div key={w} className="text-[9px] sm:text-[10px] uppercase tracking-wider text-[#676664]/60 text-center py-1.5">{w}</div>
                      ))}
                    </div>

                    <div className="grid grid-cols-7" role="grid">
                      {monthGrid.map((d, idx) => {
                        const dateStr = ymd(d.getFullYear(), d.getMonth(), d.getDate());
                        const inMonth = d.getMonth() === viewMonth;
                        const isPast = dateStr < todayStr;
                        const count = (slotsByDate[dateStr] || []).length;
                        const hasSlots = count > 0 && !isPast;
                        const isSelected = selectedDate === dateStr;
                        const isToday = dateStr === todayStr;

                        const base = "relative aspect-square flex flex-col items-center justify-center text-[13px] border-b border-r border-[#282828]/5 focus:outline-none transition-colors";
                        let cls;
                        if (!inMonth) {
                          cls = `${base} text-[#676664]/20 bg-transparent`;
                        } else if (!hasSlots) {
                          cls = `${base} text-[#676664]/25 bg-transparent cursor-not-allowed`;
                        } else if (isSelected) {
                          cls = `${base} bg-[#9A7B4F] text-white`;
                        } else {
                          // Patch 1.1 — Available cells now use a visible green tint
                          // instead of the muted default. Selected retains gold/brown.
                          cls = `${base} text-[#2F5D3B] bg-[#B8C9BD]/22 hover:bg-[#B8C9BD]/40 cursor-pointer`;
                        }

                        const dayLabel = d.getDate();
                        return (
                          <button
                            type="button"
                            key={idx}
                            role="gridcell"
                            disabled={!hasSlots || !inMonth}
                            aria-disabled={!hasSlots || !inMonth}
                            aria-label={hasSlots ? `${d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}, ${count} slot${count === 1 ? "" : "s"} available` : `${d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}, ${unavailableLabel.toLowerCase()}`}
                            title={!hasSlots && inMonth && !isPast ? unavailableLabel : undefined}
                            onClick={() => { if (hasSlots) { setSelectedDate(dateStr); setSelectedSlotId(""); } }}
                            className={cls}
                            data-testid={hasSlots ? `calendar-day-${dateStr}` : undefined}
                            data-available={hasSlots ? "true" : "false"}
                          >
                            <span className={`${isToday && !isSelected ? "underline underline-offset-2 decoration-[#9A7B4F]" : ""}`}>{dayLabel}</span>
                            {hasSlots && (
                              <span
                                className={`mt-0.5 text-[9px] leading-none px-1 py-0.5 rounded-full ${isSelected ? "bg-white/25 text-white" : "bg-[#4F8B5C]/95 text-white"}`}
                                data-testid={`calendar-count-${dateStr}`}
                              >
                                {count}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex items-center gap-4 mt-3 text-[10px] text-[#676664]/60" data-testid="calendar-legend">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#4F8B5C]/95" />
                      Available
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#9A7B4F]" />
                      Selected
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#676664]/15" />
                      {unavailableLabel}
                    </span>
                  </div>

                  {/* Patch 2.4 → 3.1 — large-gap hint. Subtle Mahra-styled banner
                      that only appears when the current viewed month has a
                      real 2-week+ gap before the next available trial date. */}
                  {gapHintTarget && (
                    <button
                      type="button"
                      onClick={jumpToNextAvailable}
                      className="mt-3 w-full text-left px-3 py-2 bg-[#B8C9BD]/15 border border-[#B8C9BD]/40 text-[12px] text-[#4F6B55] hover:bg-[#B8C9BD]/25 transition-colors flex items-center justify-between gap-3"
                      data-testid="gap-hint-banner"
                    >
                      <span className="leading-relaxed">
                        More trial appointments are available from{" "}
                        <strong className="text-[#282828]">
                          {new Date(gapHintTarget.date + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                        </strong>
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-[#4F6B55] flex-shrink-0">Jump →</span>
                    </button>
                  )}

                  {/* Time slots for selected date */}
                  {selectedDate ? (
                    <div className="mt-5" data-testid="time-slots-panel">
                      <div className="text-[11px] uppercase tracking-wider text-[#676664]/60 mb-2">
                        Available times · {new Date(selectedDate + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "2-digit", month: "long" })}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {(slotsByDate[selectedDate] || []).map(s => {
                          const selected = selectedSlotId === s.id;
                          return (
                            <button
                              type="button" key={s.id}
                              onClick={() => setSelectedSlotId(s.id)}
                              className={`inline-flex items-center gap-1 px-3 py-2 text-[12px] border transition-colors ${selected ? "bg-[#9A7B4F] border-[#9A7B4F] text-white" : "bg-white border-[#282828]/15 text-[#282828] hover:border-[#9A7B4F]"}`}
                              data-testid={`slot-option-${s.id}`}
                            >
                              <Clock className="w-3 h-3" />
                              {s.start_time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : currentMonthLoading ? (
                    <p className="mt-5 text-[12px] text-[#676664]/50 text-center" data-testid="month-loading-hint">
                      Loading available times…
                    </p>
                  ) : currentMonthHasAnySlots ? (
                    <p className="mt-5 text-[12px] text-[#676664]/60 text-center" data-testid="pick-a-date-hint">
                      Pick a highlighted date to see available times.
                    </p>
                  ) : (
                    // Patch 2.3 (Issue 2 fix) — only surfaces after the fetch
                    // for the currently-viewed month has actually settled and
                    // truly returned zero available dates. Never renders during
                    // load, so the customer no longer sees an incorrect empty
                    // state before availability arrives.
                    <p className="mt-5 text-[12px] text-[#676664]/60 text-center" data-testid="no-slots-in-month">
                      No trial appointments available in {MONTH_LABELS[viewMonth]} {viewYear}. Try another month.
                    </p>
                  )}
              </>
            </div>
          ) : (
            /* Enquiry flow: preferred_date + preferred_time (Patch 1) */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
              <div className="min-w-0 w-full">
                <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Preferred Date <span className="normal-case text-[#B85C5C]">*</span></label>
                <input type="date" value={form.preferred_date} required
                  min={melbourneToday()}
                  onChange={e => { setForm(f => ({ ...f, preferred_date: e.target.value })); handleDateInput(e); }}
                  onInvalid={handleDateInvalid}
                  onInput={handleDateInput}
                  className={`block w-full max-w-full min-w-0 box-border appearance-none border px-3 py-3 text-[13px] text-[#282828] focus:outline-none bg-transparent ${dateError ? "border-[#B85C5C] focus:border-[#B85C5C]" : "border-[#282828]/12 focus:border-[#9A7B4F]"}`}
                  style={{ WebkitAppearance: "none", MozAppearance: "none" }}
                  data-testid="trial-date"
                  aria-invalid={!!dateError}
                  aria-describedby={dateError ? "trial-date-error" : undefined}
                />
                {dateError && (
                  <p id="trial-date-error" role="alert" className="mt-1.5 text-[11px] text-[#B85C5C] leading-snug" data-testid="trial-date-error">
                    {dateError}
                  </p>
                )}
              </div>
              <div className="min-w-0 w-full">
                <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Preferred Time <span className="normal-case text-[#B85C5C]">*</span></label>
                <input type="time" value={form.preferred_time} required
                  onChange={e => setForm(f => ({ ...f, preferred_time: e.target.value }))}
                  className="block w-full max-w-full min-w-0 box-border appearance-none border border-[#282828]/12 px-3 py-3 text-[13px] text-[#282828] focus:border-[#9A7B4F] focus:outline-none bg-transparent"
                  style={{ WebkitAppearance: "none", MozAppearance: "none" }}
                  data-testid="trial-time"
                />
              </div>
            </div>
          )}

          <div className="w-full">
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#676664]/50 block mb-1.5">Notes <span className="normal-case text-[#676664]/30">(optional)</span></label>
            <textarea value={form.notes} rows={3}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full max-w-full box-border border border-[#282828]/12 px-3 py-3 text-[13px] text-[#282828] focus:border-[#9A7B4F] focus:outline-none bg-transparent resize-none"
              placeholder="Any outfits you'd like to try? Occasion details?"
              data-testid="trial-notes"
            />
          </div>

          <button type="submit" disabled={submitting || (useDirectFlow && !selectedSlotId)}
            className="w-full max-w-full box-border py-3.5 bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[12px] tracking-[0.14em] uppercase transition-colors disabled:opacity-50"
            data-testid="trial-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : (useDirectFlow ? (trialFeeEnabled ? `Pay & Book Trial · $${Number(trialFeeAmount).toFixed(2)}` : "Confirm Trial Booking") : "Submit Enquiry")}
          </button>

          {!useDirectFlow && (
            <p className="text-[10px] text-center text-[#676664]/40">
              We&apos;ll be in touch within {responseHours} {responseHours === 1 ? "hour" : "hours"} to arrange your trial appointment.
            </p>
          )}
          {useDirectFlow && !trialFeeEnabled && (
            <p className="text-[10px] text-center text-[#676664]/40">
              Your slot is confirmed instantly once submitted.
            </p>
          )}
          {useDirectFlow && trialFeeEnabled && (
            <p className="text-[10px] text-center text-[#676664]/40">
              You&apos;ll be redirected to Stripe to complete payment. Your slot is held for 15 minutes.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
